// Minimal bilingual (RU/EN) language resolution. No heavy i18n framework — the
// app only needs RU/EN for the tutorial hint catalog (and is ready to grow).
// Language comes from the user's settings (adhesion-settings.language); 'auto'
// (the default) falls back to the browser locale.

export type Lang = 'en' | 'ru';

import { STRINGS, type StringKey } from './strings';
import { readSettings } from './settings';
export type { StringKey };

/** Values interpolated into `{placeholder}` slots of a catalog string. */
export type TParams = Record<string, string | number>;

/**
 * #D5: translate a catalog key for the given language. Falls back to English,
 * then to the raw key, so a missing translation is visible but never crashes.
 *
 * Optional `params` interpolate `{name}` placeholders (e.g.
 * t('trap.result.captured', lang, { id: 3 })). Unknown placeholders are left
 * intact so a missing param is visible rather than silently blank.
 */
export function t(key: StringKey, lang: Lang, params?: TParams): string {
  const entry = STRINGS[key];
  const template = entry ? (entry[lang] ?? entry.en ?? (key as string)) : (key as string);
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    params[name] === undefined ? `{${name}}` : String(params[name])
  );
}

function fromNavigator(): Lang {
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
  }
  return 'en';
}

/** Resolve the active UI language: settings override, else browser locale. */
export function getLanguage(): Lang {
  const lang = readSettings().language;
  // 'auto' or anything else -> browser locale
  return lang === 'en' || lang === 'ru' ? lang : fromNavigator();
}
